<template>
  <div
    class="d-flex time-picker"
    :data-testid="`${testid}-time-picker`"
  >
    <div class="picker-header" />
    <div class="timepicker-icon">
      <v-icon alt="Uhr">
        mdi-clock
      </v-icon>
    </div>
    <div class="time-label">
      <span class="hour-label">HH</span>
      <span class="divider" />
      <span class="minute-label">MM</span>
    </div>
    <div
      ref="timeSelectionRef"
      class="time-selection"
    >
      <v-list
        dense
        class="hour-list"
        :data-testid="`${testid}-time-picker-hour`"
      >
        <v-list-item
          v-for="hour in allowedHours"
          :key="'h-' + hour.value"
          :class="{
            selected: hour.value === selectedHour,
            disabled: hour.disabled,
            'current-time': isToday && hour.value === currentHour,
          }"
          @click="!hour.disabled && selectHour(hour.value)"
        >
          <v-list-item-title>
            {{ hour.text }}
          </v-list-item-title>
        </v-list-item>
      </v-list>
      <div class="divider" />
      <v-list
        dense
        class="minute-list"
        :data-testid="`${testid}-time-picker-minute`"
      >
        <v-list-item
          v-for="minute in allowedMinutes"
          :key="'m-' + minute.value"
          :class="{
            selected: minute.value === selectedMinute,
            disabled: minute.disabled,
            'current-time': isToday && selectedHour === currentHour && minute.value === roundedCurrentMinute,
          }"
          @click="!minute.disabled && selectMinute(minute.value)"
        >
          <v-list-item-title>
            {{ minute.text }}
          </v-list-item-title>
        </v-list-item>
      </v-list>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, nextTick, onMounted } from 'vue';
import DatasetStore from '@/stores/dataset/dataset-module';
import { addTimezoneOffset, Frequency } from '@/util/datetime';

interface ListItem {
  text: string;
  value: number;
  disabled?: boolean;
}

export default defineComponent({
  name: 'TimePicker',
  props: {
    modelValue: {
      type: String as () => string | null,
      default: null,
    },
    date: {
      type: Date,
      required: true,
    },
    min: {
      type: String,
      required: true,
    },
    max: {
      type: String,
      required: true,
    },
    defaultTime: {
      type: String as () => string | null,
      default: null,
    },
    testid: {
      type: String,
      required: true,
    },
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const selectedMinute = ref<number | null>(null);
    const selectedHour = ref<number | null>(null);
    const selectedTime = ref<string | null>(null);
    const timeSelectionRef = ref<HTMLElement | null>(null);
    const minDateUtc = new Date(props.min);
    const maxDateUtc = new Date(props.max);

    const freq = ref<Frequency>(DatasetStore.metadata!.frequency);

    const currentHour = computed(() => addTimezoneOffset(new Date()).getHours());
    const currentMinute = computed(() => addTimezoneOffset(new Date()).getMinutes());

    const roundedCurrentMinute = computed(() => {
      const minuteStep = getMinuteStep(freq.value);
      if (!minuteStep) {
        return 60;
      }
      const now = new Date();
      return Math.floor(now.getMinutes() / minuteStep) * minuteStep;
    });

    const emitSelectedTime = () => {
      emit('update:modelValue', selectedTime.value);
    };

    const parseTime = (time: string) => {
      const [hour, minute] = time.split(':').map(Number);
      selectedHour.value = hour;
      selectedMinute.value = minute;
      selectedTime.value = time;
    };

    watch(
      () => props.modelValue,
      (newVal) => {
        if (newVal) {
          parseTime(newVal);
        } else if (props.defaultTime) {
          parseTime(props.defaultTime);
        } else {
          parseTime('00:00');
        }
      },
      { immediate: true }
    );

    const isToday = computed(() => {
      const nowUtc = addTimezoneOffset(new Date());
      return props.date instanceof Date && props.date.toDateString() === nowUtc.toDateString();
    });

    const allowedHours = computed<ListItem[]>(() => {
      if (props.date === null) return [];
      const nowUtc = addTimezoneOffset(new Date());
      const minHour = addTimezoneOffset(props.date).toDateString() === minDateUtc.toDateString() ? minDateUtc.getHours() : 0;
      const maxHour = addTimezoneOffset(props.date).toDateString() === maxDateUtc.toDateString() ? maxDateUtc.getHours() : 23;
      const currentHourNow = isToday.value ? nowUtc.getHours() : 23;

      return Array.from({ length: 24 }, (_, hour) => ({
        text: String(hour).padStart(2, '0'),
        value: hour,
        disabled: hour < minHour || hour > maxHour || hour > currentHourNow,
      }));
    });

    const NO_MINUTE_FREQUENCIES = new Set([
      '1D',
      'P1D',
      '1M',
      '1MS',
      'P1M',
      'QS-DEC',
      '1Y',
      '1YS',
      'P1Y',
    ]);

    function getMinuteStep(freq: string): number | null {
      if (NO_MINUTE_FREQUENCIES.has(freq)) {
        return null;
      }
      if (freq.endsWith('T')) {
        return parseInt(freq.split('T')[0], 10);
      }
      if (freq.startsWith('PT') && freq.endsWith('M')) {
        return parseInt(freq.substring(2, freq.length - 1), 10);
      }
      if (['1H', 'PT1H'].includes(freq)) {
        return 60;
      }
      return 10;
    }

    const allowedMinutes = computed<ListItem[]>(() => {
      const minuteStep = getMinuteStep(freq.value);

      if (!minuteStep) {
        return [{ text: '00', value: 0 }];
      }

      if (selectedHour.value === null || props.date === null) return [];
      const nowUtc = addTimezoneOffset(new Date());

      const minMinute =
        addTimezoneOffset(props.date).toDateString() === minDateUtc.toDateString() && selectedHour.value === minDateUtc.getHours()
          ? minDateUtc.getMinutes()
          : 0;

      const maxMinute =
        addTimezoneOffset(props.date).toDateString() === maxDateUtc.toDateString() && selectedHour.value === maxDateUtc.getHours()
          ? maxDateUtc.getMinutes()
          : 59;

      const currentMinuteNow =
        isToday.value && selectedHour.value === nowUtc.getHours() ? nowUtc.getMinutes() : 59;

      const upperLimit = Math.min(maxMinute, currentMinuteNow);

      return Array.from({ length: Math.floor(60 / minuteStep) }, (_, i) => {
        const minute = i * minuteStep;
        return {
          text: String(minute).padStart(2, '0'),
          value: minute,
          disabled: minute < minMinute || minute > upperLimit,
        };
      });
    });

    const updateSelectedTime = () => {
      if (selectedHour.value !== null) {
        const hour = String(selectedHour.value).padStart(2, '0');
        const minute =
          selectedMinute.value !== null
            ? String(selectedMinute.value).padStart(2, '0')
            : '00';

        selectedTime.value = `${hour}:${minute}`;
      }
      emitSelectedTime();
    };

    const selectHour = (hour: number) => {
      selectedHour.value = hour;
      updateSelectedTime();
    };

    const selectMinute = (minute: number) => {
      selectedMinute.value = minute;
      updateSelectedTime();
    };

    const setCurrentTime = () => {
      const now = new Date();
      let minute = now.getMinutes();
      const hour = now.getHours();

      if (freq.value === 'PT10M') {
        minute = Math.floor(minute / 10) * 10;
      } else {
        minute = 0;
      }

      selectedHour.value = null;
      selectedMinute.value = null;

      nextTick(() => {
        selectedHour.value = hour;
        selectedMinute.value = minute;
        selectedTime.value = `${String(hour).padStart(2, '0')}:${String(
          minute
        ).padStart(2, '0')}`;
        emitSelectedTime();
      });
    };

    const selectedTimeString = computed(() => {
      return selectedTime.value ?? '00:00';
    });

    const scrollToSelectedItem = (
      container: HTMLElement,
      selectedValue: number | null,
      items: { value: number }[],
    ) => {
      if (!selectedValue) return

      const index = items.findIndex((i) => i.value === selectedValue)
      if (index === -1) return

      const listItemHeight = 36;
      const gapRem = 0.5;
      const gapPx = gapRem * parseFloat(getComputedStyle(document.documentElement).fontSize)
      const scrollTop = index * (listItemHeight + gapPx) - container.clientHeight / 2 + listItemHeight / 2

      container.scrollTop = Math.max(scrollTop, 0)
    }

    onMounted(async () => {
      await nextTick()

      if (!timeSelectionRef.value) return

      const hourList = timeSelectionRef.value.querySelector<HTMLElement>('.hour-list')
      const minuteList = timeSelectionRef.value.querySelector<HTMLElement>('.minute-list')

      if (hourList && selectedHour.value !== null) {
        scrollToSelectedItem(hourList, selectedHour.value, allowedHours.value)
      }

      if (minuteList && selectedMinute.value !== null) {
        scrollToSelectedItem(minuteList, selectedMinute.value, allowedMinutes.value)
      }
    })

    return {
      selectedHour,
      selectedMinute,
      selectedTime,
      freq,
      currentHour,
      currentMinute,
      roundedCurrentMinute,
      allowedHours,
      allowedMinutes,
      selectHour,
      selectMinute,
      setCurrentTime,
      selectedTimeString,
      isToday,
      timeSelectionRef,
    };
  },
});
</script>

<style scoped>
.time-picker {
  display: flex;
  flex-direction: column;
  background-color: rgb(var(--v-theme-background));
  width: 100%;
  align-self: stretch;
  border-top-left-radius: 0;
}
.row {
  margin: 0;
}

.picker-header {
  display: flex;
  background-color: rgb(var(--v-theme-primary));
  height: 70px;
  width: 100%;
  align-items: center;
  justify-content: center;
  border-top-left-radius: 0;
  color: white;
  margin: 0;
}

.flex-container {
  display: flex;
  justify-content: center;
  align-items: center;
}

.selected-data {
  font-weight: 500;
  color: white !important;
  text-align: center;
}

.selected {
  background-color: rgb(var(--v-theme-primary));
  color: white !important;
}

.time-selection {
  display: flex;
  justify-content: space-evenly;
  border-left: solid rgb(var(--v-theme-divider)) 1px;
  height: 250px;
}

.time-label {
  display: flex;
  align-items: center;
  border-left: solid rgb(var(--v-theme-divider)) 1px;
  height: 30px;
  padding-bottom: 8px;
}

.hour-label,
.minute-label {
  width: 50%;
  text-align: center;
}

.divider {
  border-right: dotted rgb(var(--v-theme-divider)) 1px;
}

.hour-list,
.minute-list {
  overflow-y: auto;
  background-color: white;
  width: 45%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.3rem;
}

:deep(.v-list-item-title) {
  font-size: 0.875rem;
  width: 36px;
  height: 36px;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  margin: 0;
  padding: 0;
}

:deep(.v-list-item__content) {
  width: 36px;
  height: 36px;
  min-width: 0 !important;
}

:deep(.v-list-item) {
  width: 36px;
  height: 36px;
  min-height: 0 !important;
  padding: 0 !important;
  display: flex;
  align-items: center;
  justify-content: center;
}

:deep(.v-list) {
  padding-top: 4px;
  padding-bottom: 4px;
}

.v-list-item::before {
  background-color: white;
}

.v-list-item:hover:not(.selected) {
  background-color: #f1f1f1;
  font-weight: bold;
}
.disabled {
  opacity: 0.4;
  pointer-events: none;
}
.current-time:not(.selected) {
  border: 1px solid rgb(var(--v-theme-primary));
  color: rgb(var(--v-theme-primary)) !important;
  font-weight: bold;
  border-radius: 28px;
  min-height: 0;
  width: 36px;
  height: 36px;
}

.timepicker-icon {
  display: flex;
  height: 64px;
  align-items: center;
  justify-content: center;
  padding-bottom: 10px;
}
</style>